import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { CheckGlyph, delay, tone } from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import type { Preset } from '@/constants/presets';
import {
  SOCIAL_STORIES_BOT_URL,
  SOCIAL_STORIES_PROMO_IMAGE,
} from '@/constants/social-stories';

import { useTemplateTranslation } from './kit';

// Clinical documentation tools lead with their compliance credentials.
const COMPLIANCE_BADGE_SLUGS = [
  'progress-note-generator',
  'dap-note-generator',
  'soap-note-generator',
  'smart-goal-generator',
  'iep-goal-generator',
  'aba-notes-generator',
  'birp-note-generator',
];
const BAA_SLUGS = [
  'progress-note-generator',
  'dap-note-generator',
  'soap-note-generator',
  'aba-notes-generator',
  'birp-note-generator',
];

const COMPLIANCE = [
  { src: '/images/hipaa.png', label: 'HIPAA Compliant' },
  { src: '/images/iso-27001.png', label: 'ISO 27001 Certified' },
  { src: '/images/soc2.png', label: 'SOC2 Certified' },
];

/**
 * The top of the page: breadcrumb, title and the tool itself (passed in as
 * `children`), then a short line of reassurance.
 */
export default function TemplateHero({
  preset,
  children,
}: {
  preset: Preset;
  children: ReactNode;
}) {
  const { t } = useTemplateTranslation();
  const title = t(preset.title) || preset.title;

  const eyebrow = preset.isFree
    ? t('No sign-up needed')
    : preset.onlyForPaidPlans
      ? t('Try it with a free account')
      : t('With a free account');

  const reassurance = [
    t('No credit card required'),
    preset.hideLanguageSelector ? null : t('Supports 40+ languages'),
    t('Runs on leading AI models'),
    t('3.4M+ people have signed up'),
  ].filter((item): item is string => !!item);

  return (
    <header className='relative'>
      <LandingPageNavigation skipUserContext />

      <div className='mx-auto max-w-6xl px-4 pt-6 sm:px-6 sm:pt-8 lg:px-8'>
        <nav aria-label={t('Breadcrumb')} className='text-[13px]'>
          <ol className='flex min-w-0 items-center gap-2'>
            <li className='flex-none'>
              <Link
                href='/templates'
                className={clsxm(
                  'transition-colors hover:text-[#10101c] dark:hover:text-white',
                  tone.quiet,
                )}
              >
                {t('Templates')}
              </Link>
            </li>
            <li aria-hidden='true' className={tone.quiet}>
              /
            </li>
            <li
              aria-current='page'
              className={clsxm('min-w-0 truncate font-medium', tone.body)}
            >
              {title}
            </li>
          </ol>
        </nav>

        <div className='mx-auto mt-8 max-w-5xl text-center sm:mt-10'>
          {preset.slug === 'social-stories-generator' ? (
            <Link
              href={SOCIAL_STORIES_BOT_URL}
              className={clsxm(
                styles.enter,
                'mb-6 inline-flex items-center gap-2.5 rounded-full bg-white py-1 pe-4 ps-1 text-sm font-medium text-primary-700 shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] transition hover:ring-primary-300 dark:bg-white/[0.05] dark:text-primary-200 dark:ring-white/10',
              )}
            >
              <Image
                src={SOCIAL_STORIES_PROMO_IMAGE}
                alt=''
                width={32}
                height={32}
                className='rounded-full'
              />
              New: Create a fully illustrated PDF picture book with AI
              <span aria-hidden='true'>&rarr;</span>
            </Link>
          ) : null}

          <p
            className={clsxm(
              styles.enter,
              'inline-flex items-center gap-2.5 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
              tone.body,
            )}
          >
            <span className='rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
              {t('Free')}
            </span>
            {eyebrow}
          </p>

          <h1
            className={clsxm(
              styles.display,
              styles.enter,
              'mt-6 text-[2.5rem] font-semibold leading-[1.05] sm:text-6xl sm:leading-[1.02] lg:text-[4rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            {title}
          </h1>

          <p
            className={clsxm(
              styles.enter,
              'mx-auto mt-5 max-w-3xl text-pretty text-lg leading-8 sm:text-xl sm:leading-8',
              tone.body,
            )}
            style={delay(120)}
          >
            {t(preset.description) || preset.description}
          </p>

          {COMPLIANCE_BADGE_SLUGS.includes(preset.slug) ? (
            <ul
              className={clsxm(
                styles.enter,
                'mt-6 flex flex-wrap items-center justify-center gap-2',
              )}
              style={delay(160)}
            >
              {COMPLIANCE.map((badge) => (
                <li
                  key={badge.label}
                  className={clsxm(
                    'inline-flex items-center gap-2 rounded-full bg-white py-1 pe-3 ps-1 text-[13px] font-medium ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
                    tone.body,
                  )}
                >
                  <Image
                    src={badge.src}
                    width={24}
                    height={24}
                    alt=''
                    className='rounded-full'
                  />
                  {badge.label}
                </li>
              ))}
              {BAA_SLUGS.includes(preset.slug) ? (
                <li>
                  <Link
                    href='/settings/baa'
                    className='inline-flex items-center gap-1 rounded-full px-3 py-1 text-[13px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300'
                  >
                    Review &amp; sign your BAA
                    <span aria-hidden='true'>&rarr;</span>
                  </Link>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      </div>

      <div
        className={clsxm(
          styles.enter,
          'mx-auto mt-10 max-w-6xl px-2 sm:mt-12 sm:px-6 lg:px-8',
        )}
        style={delay(180)}
      >
        {children}
      </div>

      <ul
        className={clsxm(
          'mx-auto mt-6 flex max-w-6xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 text-[13px] sm:px-6 lg:px-8',
          tone.quiet,
        )}
      >
        {reassurance.map((item) => (
          <li key={item} className='inline-flex items-center gap-1.5'>
            <CheckGlyph className='h-3.5 w-3.5 text-emerald-500' />
            {item}
          </li>
        ))}
      </ul>
    </header>
  );
}
